/**
 * Session lifecycle: sign up, sign in, sign out, verification, password reset.
 *
 * A thin, typed wrapper over the Better Auth client so the pages keep calling
 * `signIn({ email, password })` rather than learning a new API shape. What
 * disappeared relative to the old module: the token set, the single-flight
 * refresh dedupe, and the manual OTP request/verify pair. Better Auth owns all of
 * it, and the session is a cookie the browser never has to handle.
 */
import { PASSWORD_MIN_LENGTH } from '@cwy/config/contracts';
import { authClient } from '@/lib/authClient';
import { clearAccount } from '@/lib/auth';
import { LOGIN_PATH, currentSurface, tenantPath } from '@/lib/tenant';
import { resetIdentityQueries } from '@/hooks/useSession';

export interface Credentials {
  email: string;
  password: string;
}

/**
 * Re-exported, not redeclared. The server enforces the same number through
 * `zPassword` and Better Auth, and a second copy here is what let the B2C
 * sign-up and the staff password change disagree.
 */
export const MIN_PASSWORD_LENGTH = PASSWORD_MIN_LENGTH;

/** Better Auth must return email-link navigations to the SPA, not the API origin. */
function emailVerificationCallbackURL(): string {
  return `${window.location.origin}/email-verification?status=success`;
}

/** Better Auth returns `{ data, error }` rather than throwing. */
function assertOk<T>(result: { data: T; error: { message?: string } | null }): T {
  if (result.error) {
    throw new Error(result.error.message ?? 'Something went wrong. Please try again.');
  }
  return result.data;
}

export async function signUp({
  email,
  password,
  name,
}: Credentials & { name?: string }): Promise<void> {
  assertOk(
    await authClient.signUp.email({
      email,
      password,
      // Better Auth requires a name; the questionnaire collects the real one and
      // PUT /me/profile overwrites this.
      name: name?.trim() || email.split('@')[0],
      callbackURL: emailVerificationCallbackURL(),
    }),
  );
  resetIdentityQueries();
}

export async function signIn({ email, password }: Credentials): Promise<void> {
  assertOk(await authClient.signIn.email({ email, password }));
  resetIdentityQueries();
}

/**
 * Sign out, then hard-navigate to the login this person came in through.
 *
 * The full page load is deliberate: it is the simplest way to guarantee no
 * react-query cache entry from the previous user survives into the next session.
 *
 * The destination is the CURRENT surface and the CURRENT workspace, not `/login`.
 * A hard-coded student path sent a school admin from their own console to the
 * first-party student sign-in — a different login, in a different tenant, from the
 * one they were using a moment ago.
 */
export async function signOut(): Promise<void> {
  await authClient.signOut().catch(() => undefined);
  clearAccount();
  resetIdentityQueries();
  window.location.assign(tenantPath(LOGIN_PATH[currentSurface()]));
}

/** Re-send the verification email. */
export async function resendVerification(email: string): Promise<void> {
  assertOk(
    await authClient.sendVerificationEmail({
      email,
      callbackURL: emailVerificationCallbackURL(),
    }),
  );
}

/** Start a password reset. */
export async function requestPasswordReset(email: string): Promise<void> {
  assertOk(
    await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    }),
  );
}

/** Finish a password reset with the token from the emailed link. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  assertOk(await authClient.resetPassword({ token, newPassword }));
}

/** Change the password of the signed-in user. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  assertOk(
    await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    }),
  );
}
